import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  UserGroupIcon, 
  PlusIcon, 
  TrashIcon, 
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  DocumentTextIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

import { useAuth } from '../../hooks/useAuth';
import api, { packagesAPI } from '../../services/api';
import { bulkOrderService, BulkOrder, BulkOrderRecipient, BulkOrderCreateValidation } from '../../services/bulkOrderService';
import { NETWORKS, PAYMENT_METHODS } from '../../utils/constants';
import { sanitizeUserFacingErrorMessage } from '../../utils/notificationManager';
import { normalizeGhanaPhone, sanitizePhoneInput, validateGhanaLocalPhone } from '../../utils/phoneValidation';

import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import NetworkLogo from '../../components/common/NetworkLogo';
import { savedGroupsAPI, SavedGroup } from '../../services/savedGroupsAPI';

interface BulkOrderFormData {
  recipients: BulkOrderRecipient[];
  paymentMethod: typeof PAYMENT_METHODS.WALLET | typeof PAYMENT_METHODS.PAYSTACK | typeof PAYMENT_METHODS.MOBILE_MONEY;
  notes: string;
}

const BulkOrderPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateWalletBalance } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<any[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<string>('mtn');
  const [recipients, setRecipients] = useState<BulkOrderRecipient[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [phoneInput, setPhoneInput] = useState<string>('');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [processingOrder, setProcessingOrder] = useState<boolean>(false);
  const [currentBulkOrder, setCurrentBulkOrder] = useState<BulkOrder | null>(null);
  const [createValidation, setCreateValidation] = useState<BulkOrderCreateValidation | null>(null);
  const [createRequestId, setCreateRequestId] = useState<string | null>(null);
  const [paymentAvailability, setPaymentAvailability] = useState<{ paystackEnabled: boolean; mobileMoneyEnabled: boolean }>(
    {
      paystackEnabled: false,
      mobileMoneyEnabled: false
    }
  );
  const [, setPaymentAvailabilityLoaded] = useState(false);
  const [mobileMoneyNumber, setMobileMoneyNumber] = useState<string>('');
  const [mobileMoneyNetwork, setMobileMoneyNetwork] = useState<'mtn' | 'airteltigo' | 'telecel'>('mtn');
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'size' | 'popularity'>('price');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showPopularOnly, setShowPopularOnly] = useState<boolean>(false);
  const [priceRange, setPriceRange] = useState<{ min: number; max: number }>({ min: 0, max: 1000 });
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Saved Groups state
  const [groups, setGroups] = useState<SavedGroup[]>([]);
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  const [loadedFromGroupId, setLoadedFromGroupId] = useState<string | null>(null);
  const [pendingGroupMembers, setPendingGroupMembers] = useState<{ phone: string; name: string; network: 'mtn' | 'airteltigo' | 'telecel'; groupName: string }[]>([]);
  const [showSaveGroupModal, setShowSaveGroupModal] = useState(false);
  const [saveGroupName, setSaveGroupName] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);
  const [pendingNavigateOrderId, setPendingNavigateOrderId] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<BulkOrderFormData>({
    recipients: [],
    paymentMethod: PAYMENT_METHODS.WALLET,
    notes: ''
  });

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await packagesAPI.getPackages({ 
        network: selectedNetwork,
        includeDiscounts: 'true'
      });
      
      // Handle different response structures
      let packagesData = [];
      const responseData = response.data as any;
      
      if (responseData.data && responseData.data.packages) {
        // New ErrorHandler response format
        packagesData = responseData.data.packages;
      } else if (responseData.packages) {
        // Direct response format
        packagesData = responseData.packages;
      } else if (Array.isArray(responseData)) {
        // Array response format
        packagesData = responseData;
      }
      
      setPackages(packagesData || []);
    } catch (error) {

      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [selectedNetwork]);

  // Load available packages
  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    const loadPaymentAvailability = async () => {
      try {
        const resp = await api.get('/config/app');
        const cfg = (resp as any)?.data?.config;
        const payments = cfg?.payments;

        if (payments && typeof payments === 'object') {
          const resolved = {
            paystackEnabled: payments?.paystackEnabled ?? true,
            mobileMoneyEnabled: payments?.mobileMoneyEnabled ?? true
          };

          setPaymentAvailability(resolved);

          setFormData((prev) => {
            if (prev.paymentMethod === 'paystack' && !resolved.paystackEnabled) {
              return {
                ...prev,
                paymentMethod: resolved.mobileMoneyEnabled ? PAYMENT_METHODS.MOBILE_MONEY : PAYMENT_METHODS.WALLET
              };
            }
            if (prev.paymentMethod === 'mobile_money' && !resolved.mobileMoneyEnabled) {
              return {
                ...prev,
                paymentMethod: resolved.paystackEnabled ? PAYMENT_METHODS.PAYSTACK : PAYMENT_METHODS.WALLET
              };
            }
            return prev;
          });
        }
      } catch {
      } finally {
        setPaymentAvailabilityLoaded(true);
      }
    };

    loadPaymentAvailability();
  }, []);

  // Load saved groups on mount (non-blocking)
  useEffect(() => {
    let alive = true;
    savedGroupsAPI.getAll()
      .then(res => { if (alive) setGroups(res.data.groups || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Queue group members when navigated from GroupsPage
  useEffect(() => {
    const fromGroup = (location.state as { fromGroup?: SavedGroup } | null)?.fromGroup;
    if (fromGroup && fromGroup.members.length > 0) {
      setPendingGroupMembers(fromGroup.members.map(m => ({ ...m, groupName: fromGroup.name })));
      setLoadedFromGroupId(fromGroup._id);
      setGroupsExpanded(false);
      toast.success(`"${fromGroup.name}" queued — select a package then click "Add All"`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadGroup = (group: SavedGroup) => {
    savedGroupsAPI.markUsed(group._id).catch(() => {});
    setPendingGroupMembers(group.members.map(m => ({ ...m, groupName: group.name })));
    setLoadedFromGroupId(group._id);
    setGroupsExpanded(false);
    toast.success(`"${group.name}" queued — select a package then click "Add All"`);
  };

  const addAllGroupMembers = () => {
    if (!selectedPackage) { toast.error('Select a package first'); return; }
    if (pendingGroupMembers.length === 0) return;
    let added = 0;
    let skipped = 0;
    for (const m of pendingGroupMembers) {
      const isDuplicate = recipients.some(r => r.phone === m.phone);
      if (isDuplicate) { skipped++; continue; }
      recipients.push({
        phone: m.phone,
        network: m.network,
        dataPackageId: selectedPackage.id,
        dataPackage: {
          _id: selectedPackage.id,
          name: selectedPackage.name,
          size: selectedPackage.size,
          validity: selectedPackage.validity,
          pricing: selectedPackage.pricing
        }
      });
      added++;
    }
    setRecipients([...recipients]);
    setPendingGroupMembers([]);
    if (skipped > 0) toast.success(`Added ${added} member${added !== 1 ? 's' : ''} (${skipped} duplicate${skipped !== 1 ? 's' : ''} skipped)`);
    else toast.success(`Added ${added} member${added !== 1 ? 's' : ''} from group`);
  };

  // Filter and sort packages
  const getFilteredAndSortedPackages = () => {
    let filteredPackages = [...packages];

    // Apply search filter
    if (searchTerm) {
      filteredPackages = filteredPackages.filter(pkg =>
        pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.size.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.validity.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply popular filter
    if (showPopularOnly) {
      filteredPackages = filteredPackages.filter(pkg => pkg.isPopular);
    }

    // Apply price range filter
    filteredPackages = filteredPackages.filter(pkg => {
      const price = user?.role === 'agent' ? pkg.pricing?.agent : pkg.pricing?.user;
      return price >= priceRange.min && price <= priceRange.max;
    });

    // Apply sorting
    filteredPackages.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'price':
          const priceA = user?.role === 'agent' ? a.pricing?.agent : a.pricing?.user;
          const priceB = user?.role === 'agent' ? b.pricing?.agent : b.pricing?.user;
          comparison = priceA - priceB;
          break;
        case 'size':
          // Extract numeric value from size (e.g., "1GB" -> 1, "500MB" -> 0.5)
          const sizeA = parseFloat(a.size.replace(/[^\d.]/g, ''));
          const sizeB = parseFloat(b.size.replace(/[^\d.]/g, ''));
          comparison = sizeA - sizeB;
          break;
        case 'popularity':
          comparison = (b.isPopular ? 1 : 0) - (a.isPopular ? 1 : 0);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filteredPackages;
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('price');
    setSortOrder('asc');
    setShowPopularOnly(false);
    setPriceRange({ min: 0, max: 1000 });
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (showPopularOnly) count++;
    if (priceRange.min > 0 || priceRange.max < 1000) count++;
    if (sortBy !== 'name' || sortOrder !== 'asc') count++;
    return count;
  };

  // Add single recipient with selected package
  const addRecipient = () => {
    if (!selectedPackage) {
      toast.error('Please select a package first');
      return;
    }

    if (!phoneInput.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    const phone = sanitizePhoneInput(phoneInput);
    const phoneCheck = validateGhanaLocalPhone(phone);
    if (!phoneCheck.isValid || !phoneCheck.normalized) {
      toast.error(phoneCheck.error || 'Enter a valid Ghana phone number (e.g. 0534788219)');
      return;
    }

    const normalizedPhone = phoneCheck.normalized;

    // Check for duplicates - STRICT: One package per phone number in bulk orders
    const isDuplicate = recipients.some(r => r.phone === normalizedPhone);
    if (isDuplicate) {
      toast.error('❌ Duplicate phone number! Each number can only appear once in a bulk order. Please remove the duplicate or use a different number.');
      return;
    }

    // Create new recipient with selected package
    const newRecipient: BulkOrderRecipient = {
      phone: normalizedPhone,
      network: selectedNetwork as any,
      dataPackageId: selectedPackage.id,
      dataPackage: {
        _id: selectedPackage.id,
        name: selectedPackage.name,
        size: selectedPackage.size,
        validity: selectedPackage.validity,
        pricing: selectedPackage.pricing
      }
    };

    setRecipients([...recipients, newRecipient]);
    setPhoneInput(''); // Clear phone input for next entry
    
    toast.success(`Added ${normalizedPhone} with ${selectedPackage.name} package`);
  };

  // Remove recipient
  const removeRecipient = (phone: string) => {
    setRecipients(recipients.filter((r: BulkOrderRecipient) => r.phone !== phone));
  };

  // Clear all recipients
  const clearRecipients = () => {
    setRecipients([]);
  };

  // Calculate total price
  const calculateTotal = () => {
    return recipients.reduce((total, recipient) => {
      const unitPrice = recipient.dataPackage?.pricing?.agent || recipient.dataPackage?.pricing?.user || 0;
      return total + unitPrice;
    }, 0);
  };

  // Preview bulk order
  const previewBulkOrder = () => {
    if (recipients.length === 0) {
      toast.error('Please add recipients');
      return;
    }
    
    // Check if all recipients have packages assigned
    const recipientsWithoutPackages = recipients.filter(r => !r.dataPackageId);
    if (recipientsWithoutPackages.length > 0) {
      toast.error('Some recipients don\'t have packages assigned');
      return;
    }

    setFormData({
      recipients,
      paymentMethod: 'wallet',
      notes: ''
    });

    setShowPreview(true);
  };

  // Create bulk order
  const createBulkOrder = async () => {
    try {
      setProcessingOrder(true);
      setCreateValidation(null);
      setCreateRequestId(null);
      
      const createResult = await bulkOrderService.createBulkOrder(formData);
      const bulkOrder = createResult.bulkOrder;

      setCreateValidation(createResult.validation || null);
      setCreateRequestId(createResult.requestId || null);
      setRecipients(bulkOrder.recipients);
      setFormData((prev) => ({ ...prev, recipients: bulkOrder.recipients }));
      setCurrentBulkOrder(bulkOrder);
      setShowPreview(false);
      setShowPaymentModal(true);

      if (createResult.validation?.rejected) {
        toast.error(
          `${createResult.validation.rejected} recipient(s) were rejected. ` +
          `${createResult.validation.accepted} will proceed.`
        );
      } else {
        toast.success('Order placed successfully.');
      }
    } catch (error: any) {

      toast.error(sanitizeUserFacingErrorMessage(error.response?.data?.message || 'Failed to create bulk order'));
    } finally {
      setProcessingOrder(false);
    }
  };

  // Handle network selection
  const handleNetworkSelection = (networkName: string) => {
    setSelectedNetwork(networkName);
  };

  // Process payment
  const processPayment = async () => {
    if (!currentBulkOrder) return;

    try {
      setProcessingOrder(true);

      if (formData.paymentMethod === PAYMENT_METHODS.PAYSTACK) {
        if (!paymentAvailability.paystackEnabled) {
          toast.error('Paystack payments are temporarily unavailable. Please use Mobile Money.');
          return;
        }

        const email = String(user?.email || '').trim();
        if (!email) {
          toast.error('Your account email is required for Paystack payments.');
          return;
        }

        toast.loading('Redirecting to Paystack...', { id: 'bulk-paystack-init' });
        const updatedOrder = await bulkOrderService.processPayment(currentBulkOrder._id, {
          paymentMethod: PAYMENT_METHODS.PAYSTACK,
          email
        });

        const authUrl = (updatedOrder as any)?.authorization_url;
        if (!authUrl) {
          toast.error('Paystack initialization failed (missing authorization URL).', { id: 'bulk-paystack-init' });
          return;
        }

        toast.success('Redirecting...', { id: 'bulk-paystack-init' });
        window.location.href = String(authUrl);
        return;
      }

      if (formData.paymentMethod === PAYMENT_METHODS.MOBILE_MONEY) {
        if (!paymentAvailability.mobileMoneyEnabled) {
          toast.error('Mobile Money payments are temporarily unavailable.');
          return;
        }

        const normalizedPhone = normalizeGhanaPhone(mobileMoneyNumber);
        if (!normalizedPhone) {
          toast.error('Enter a valid Ghana phone number in local format only (e.g. 0534788219).');
          return;
        }

        if (!mobileMoneyNetwork) {
          toast.error('Select your mobile money network.');
          return;
        }

        const updatedOrder = await bulkOrderService.processPayment(currentBulkOrder._id, {
          paymentMethod: PAYMENT_METHODS.MOBILE_MONEY,
          phoneNumber: normalizedPhone,
          network: mobileMoneyNetwork
        });

        setCurrentBulkOrder(updatedOrder);
        setShowPaymentModal(false);
        toast.success('Mobile money payment initiated. Payment will be auto-detected within seconds.');
        navigate(`/bulk-orders/${updatedOrder._id}`);
        return;
      }
      
      const updatedOrder = await bulkOrderService.processPayment(
        currentBulkOrder._id,
        { paymentMethod: PAYMENT_METHODS.WALLET }
      );

      // Refresh wallet in UI immediately if backend returned authoritative new balance
      const maybeResponse: any = (updatedOrder as any);
      const newBalance = maybeResponse?.newBalance;
      if (typeof newBalance === 'number' && updateWalletBalance) {
        updateWalletBalance(newBalance);
      }
      
      setCurrentBulkOrder(updatedOrder);
      setShowPaymentModal(false);

      toast.success('Order placed successfully.');

      if (!loadedFromGroupId && recipients.length > 1) {
        setPendingNavigateOrderId(updatedOrder._id);
        setSaveGroupName('');
        setShowSaveGroupModal(true);
      } else {
        setTimeout(() => {
          navigate(`/bulk-orders/${updatedOrder._id}`);
        }, 2000);
      }
      
    } catch (error: any) {

      toast.error(sanitizeUserFacingErrorMessage(error.response?.data?.message || 'Payment failed'));
    } finally {
      setProcessingOrder(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Bulk Orders
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Place bulk orders for multiple recipients across all networks
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/groups')}
                className="w-full sm:w-auto"
              >
                <UserGroupIcon className="h-5 w-5 mr-2" />
                My Groups
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/bulk-orders/history')}
                className="w-full sm:w-auto"
              >
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Order History
              </Button>
            </div>
          </div>

          {/* Network Tabs - Mobile First */}
          <div className="grid grid-cols-3 gap-2 bg-gray-100 dark:bg-gray-800 p-2 rounded-xl">
            {Object.entries(NETWORKS).map(([key, network]) => {
              const isActive = selectedNetwork === (network as any).name;
              const networkColors = {
                mtn: 'bg-yellow-500 text-black hover:bg-yellow-600',
                airteltigo: 'bg-blue-600 text-white hover:bg-blue-700',
                telecel: 'bg-red-600 text-white hover:bg-red-700'
              };
              const networkName = (network as any).name;
              
              return (
                <button
                  key={key}
                  onClick={() => handleNetworkSelection(networkName)}
                  className={`px-3 sm:px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                    isActive
                      ? networkColors[networkName as keyof typeof networkColors] + ' shadow-md'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 min-w-0">
                    <NetworkLogo network={networkName} size="sm" />
                    <span className="text-sm sm:text-base min-w-0 truncate">{(network as any).displayName}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Saved Groups Strip */}
        {groups.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => setGroupsExpanded(e => !e)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <UserGroupIcon className="h-4 w-4" />
              Your Groups ({groups.length})
              {groupsExpanded
                ? <span className="text-xs text-gray-400">(collapse)</span>
                : <span className="text-xs text-gray-400">(click to expand)</span>
              }
            </button>
            {groupsExpanded && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {groups.map(group => (
                  <button
                    key={group._id}
                    onClick={() => handleLoadGroup(group)}
                    className={`flex-shrink-0 w-44 text-left rounded-xl border-2 p-3 transition-all hover:shadow-md
                      ${loadedFromGroupId === group._id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600'
                      }`}
                  >
                    <div className="text-2xl mb-1">{group.emoji}</div>
                    <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">{group.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                    </div>
                    {loadedFromGroupId === group._id && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">Active</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 lg:gap-8">
          {/* Left Column - Package Selection */}
          <div className="lg:col-span-5 space-y-4 md:space-y-6">
            {/* Selected Network Info */}
            <Card className={`p-4 md:p-6 border-2 ${
              selectedNetwork === 'mtn' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
              selectedNetwork === 'airteltigo' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' :
              'border-red-500 bg-red-50 dark:bg-red-900/20'
            }`}>
              <div className="flex items-center">
                <div className="relative mr-3 md:mr-4">
                  <NetworkLogo network={selectedNetwork} size="md" className="md:w-12 md:h-12" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900 dark:text-white text-base md:text-lg mb-1">
                    {(NETWORKS[selectedNetwork.toUpperCase() as keyof typeof NETWORKS] as any).displayName}
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 font-medium">
                    {selectedNetwork.toUpperCase()} Network
                  </p>
                  <div className="flex items-center mt-1 md:mt-2">
                    <CheckCircleIcon className="h-3 w-3 md:h-4 md:w-4 text-green-500 mr-1" />
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                      Ready
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Package Selection - Step 1 - Only show when network is selected */}
            {selectedNetwork && (
              <Card className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6 min-w-0">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                    1. Select Package
                  </h2>
                  {getActiveFiltersCount() > 0 && (
                    <Badge variant="primary" size="sm">
                      {getFilteredAndSortedPackages().length} of {packages.length}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={selectedNetwork ? 'primary' : 'secondary'}>
                    {NETWORKS[selectedNetwork as keyof typeof NETWORKS]?.displayName || selectedNetwork.toUpperCase()}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="relative"
                  >
                    <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                    Filters
                    {getActiveFiltersCount() > 0 && (
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary-500 text-white text-xs rounded-full flex items-center justify-center">
                        {getActiveFiltersCount()}
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search packages by name, size, or validity..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Sort By */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sort By
                      </label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="name">Name</option>
                        <option value="price">Price</option>
                        <option value="size">Size</option>
                        <option value="popularity">Popularity</option>
                      </select>
                    </div>

                    {/* Sort Order */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Order
                      </label>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                      </select>
                    </div>

                    {/* Price Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Price (GHS)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1000"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) || 1000 })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    {/* Popular Filter */}
                    <div className="flex items-end">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showPopularOnly}
                          onChange={(e) => setShowPopularOnly(e.target.checked)}
                          className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Popular only
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                    >
                      <XMarkIcon className="h-4 w-4 mr-2" />
                      Clear All Filters
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Results Summary */}
              {getActiveFiltersCount() > 0 && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Showing {getFilteredAndSortedPackages().length} of {packages.length} packages
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      Clear filters
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[520px] md:max-h-96 overflow-y-auto pr-1 md:pr-2">
                {getFilteredAndSortedPackages().map((pkg) => (
                  <motion.button
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-3 sm:p-4 md:p-5 rounded-xl border-2 transition-all duration-200 text-left overflow-hidden group ${
                      selectedPackage?.id === pkg.id
                        ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/20 shadow-lg shadow-primary-200/50 dark:shadow-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-300 hover:shadow-md'
                    }`}
                  >
                    {/* Selection Indicator */}
                    {selectedPackage?.id === pkg.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-3 right-3"
                      >
                        <div className="flex items-center justify-center w-6 h-6 bg-primary-500 rounded-full">
                          <CheckCircleIcon className="h-4 w-4 text-white" />
                        </div>
                      </motion.div>
                    )}

                    {/* Popular Badge */}
                    {pkg.isPopular && (
                      <div className="absolute top-3 left-3">
                        <Badge variant="warning" size="sm" className="text-xs font-medium">
                          ⭐ Popular
                        </Badge>
                      </div>
                    )}

                    {/* Package Content */}
                    <div className="mt-8">
                      {/* Network Icon */}
                      <div className="flex items-center mb-3">
                        <NetworkLogo network={selectedNetwork} size="sm" />
                        <span className="ml-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          {selectedNetwork.toUpperCase()}
                        </span>
                      </div>

                      {/* Package Name */}
                      <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-lg leading-tight">
                        {pkg.name}
                      </h3>

                      {/* Package Details */}
                      <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 min-w-0">
                          <span className="min-w-0 break-words">{pkg.size}</span>
                          <span className="text-gray-500 dark:text-gray-400 min-w-0 break-words sm:text-right">{pkg.validity}</span>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 min-w-0">
                          <div className="min-w-0">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              {user?.role === 'agent' ? 'Agent Price' : 'User Price'}
                            </span>
                            <div className="flex items-baseline">
                              <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                                GHS {user?.role === 'agent' ? pkg.pricing?.agent : pkg.pricing?.user}
                              </span>
                            </div>
                          </div>
                          {selectedPackage?.id !== pkg.id && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <ArrowRightIcon className="h-5 w-5 text-primary-500" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Hover Effect Overlay */}
                      {selectedPackage?.id !== pkg.id && (
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-primary-600/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>

              {getFilteredAndSortedPackages().length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    {packages.length === 0 ? (
                      <ExclamationTriangleIcon className="h-8 w-8 text-gray-400" />
                    ) : (
                      <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 mb-2">
                    {packages.length === 0 
                      ? `No packages available for ${NETWORKS[selectedNetwork as keyof typeof NETWORKS]?.displayName || selectedNetwork.toUpperCase()}`
                      : 'No packages found matching your criteria'
                    }
                  </p>
                  {packages.length > 0 && getActiveFiltersCount() > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      className="mt-3"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              )}
            </Card>
            )}

            {/* Add Recipient - Step 2 - Only show when network is selected */}
            {selectedNetwork && (
              <Card className="p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                2. Add Recipient
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Recipient Phone Number
                  </label>
                  <Input
                    type="tel"
                    placeholder="0534788219"
                    value={phoneInput}
                    onChange={(e) => {
                      const raw = sanitizePhoneInput(e.target.value);
                      const normalized = normalizeGhanaPhone(raw);
                      setPhoneInput(normalized || raw);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addRecipient();
                      }
                    }}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {selectedNetwork === 'mtn' ? 'MTN numbers: 024, 054, 055, 059' : selectedNetwork === 'airteltigo' ? 'AirtelTigo numbers: 026, 056, 027, 057' : 'Telecel numbers: 020, 050'}
                  </p>
                </div>

                {selectedPackage && (
                  <div className={`p-3 rounded-lg border-l-4 ${
                    selectedNetwork === 'mtn' ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-500' :
                    selectedNetwork === 'airteltigo' ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-500' :
                    'bg-red-50 dark:bg-red-900/10 border-red-500'
                  }`}>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      Selected: {selectedPackage.name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {selectedPackage.size} • {selectedPackage.validity} • 
                      <span className={`font-bold ml-1 ${
                        selectedNetwork === 'mtn' ? 'text-yellow-700 dark:text-yellow-400' :
                        selectedNetwork === 'airteltigo' ? 'text-blue-700 dark:text-blue-400' :
                        'text-red-700 dark:text-red-400'
                      }`}>
                        GHS {user?.role === 'agent' ? selectedPackage.pricing?.agent : selectedPackage.pricing?.user}
                      </span>
                    </p>
                  </div>
                )}

                <Button
                  variant="primary"
                  onClick={addRecipient}
                  disabled={!selectedPackage || !phoneInput.trim()}
                  className="w-full"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Recipient
                </Button>

                {pendingGroupMembers.length > 0 && (
                  <div className="rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-3">
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                      <UserGroupIcon className="h-4 w-4 inline mr-1" />
                      <strong>{pendingGroupMembers[0].groupName}</strong>: {pendingGroupMembers.length} member{pendingGroupMembers.length !== 1 ? 's' : ''} queued
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={addAllGroupMembers}
                      disabled={!selectedPackage}
                      className="w-full"
                    >
                      <UserGroupIcon className="h-4 w-4 mr-1.5" />
                      Add All {pendingGroupMembers.length} Members
                    </Button>
                    {!selectedPackage && (
                      <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">Select a package above first</p>
                    )}
                  </div>
                )}
              </div>
            </Card>
            )}
          </div>

          {/* Right Column - Recipients List & Order Summary - Only show when network is selected */}
          {selectedNetwork && (
            <div className="lg:col-span-7 space-y-4 md:space-y-6">
              {/* Recipients List */}
              <Card className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2 sm:gap-0">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                  3. Recipients List
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
                  </span>
                  {recipients.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearRecipients}
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>

              {recipients.length === 0 ? (
                <div className="text-center py-8 md:py-12">
                  <div className={`w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                    selectedNetwork === 'mtn' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                    selectedNetwork === 'airteltigo' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    <UserGroupIcon className={`h-8 w-8 md:h-10 md:w-10 ${
                      selectedNetwork === 'mtn' ? 'text-yellow-600 dark:text-yellow-400' :
                      selectedNetwork === 'airteltigo' ? 'text-blue-600 dark:text-blue-400' :
                      'text-red-600 dark:text-red-400'
                    }`} />
                  </div>
                  <p className="text-base md:text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No recipients added yet
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Select a {(NETWORKS[selectedNetwork.toUpperCase() as keyof typeof NETWORKS] as any).displayName} package and add recipients
                  </p>
                </div>
              ) : (
                <div className="space-y-2 md:space-y-3 max-h-[400px] md:max-h-96 overflow-y-auto">
                  {recipients.map((recipient, index) => (
                    <div
                      key={recipient.phone}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 rounded-lg border-l-4 ${
                        selectedNetwork === 'mtn' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10' :
                        selectedNetwork === 'airteltigo' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' :
                        'border-red-500 bg-red-50 dark:bg-red-900/10'
                      } shadow-sm`}
                    >
                      <div className="flex items-center space-x-3 mb-2 sm:mb-0 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold ${
                            selectedNetwork === 'mtn' ? 'bg-yellow-500 text-black' :
                            selectedNetwork === 'airteltigo' ? 'bg-blue-600 text-white' :
                            'bg-red-600 text-white'
                          }`}>
                            <span className="text-sm md:text-base">
                              {index + 1}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white text-sm md:text-base break-words whitespace-normal">
                            {recipient.phone}
                          </p>
                          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 break-words whitespace-normal">
                            {recipient.dataPackage?.name} • {recipient.dataPackage?.size}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end space-x-3 sm:space-x-4">
                        <span className={`text-base md:text-lg font-bold ${
                          selectedNetwork === 'mtn' ? 'text-yellow-700 dark:text-yellow-400' :
                          selectedNetwork === 'airteltigo' ? 'text-blue-700 dark:text-blue-400' :
                          'text-red-700 dark:text-red-400'
                        }`}>
                          GHS {user?.role === 'agent' 
                            ? recipient.dataPackage?.pricing?.agent 
                            : recipient.dataPackage?.pricing?.user}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeRecipient(recipient.phone)}
                          className="flex-shrink-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Order Summary & Place Order */}
            {recipients.length > 0 && (
              <div className="lg:sticky lg:top-6">
                <Card className={`p-4 md:p-6 border-2 ${
                  selectedNetwork === 'mtn' ? 'border-yellow-500' :
                  selectedNetwork === 'airteltigo' ? 'border-blue-500' :
                  'border-red-500'
                }`}>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    4. Order Summary
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-3 border-t border-gray-200 dark:border-gray-700 min-w-0">
                      <span className="text-gray-600 dark:text-gray-400">
                        Total Recipients
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white min-w-0 break-words sm:text-right">
                        {recipients.length}
                      </span>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-3 border-t border-gray-200 dark:border-gray-700 min-w-0">
                      <span className="text-gray-600 dark:text-gray-400">
                        Total Amount
                      </span>
                      <span className="text-2xl font-bold text-primary-600 min-w-0 break-words sm:text-right">
                        GHS {calculateTotal().toFixed(2)}
                      </span>
                    </div>

                    <div className="pt-4">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={previewBulkOrder}
                        className="w-full"
                      >
                        <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                        Place Order
                        <ArrowRightIcon className="h-5 w-5 ml-2" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Preview Modal */}
        <Modal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          title="Preview Bulk Order"
          size="large"
        >
          <div className="space-y-6">
            {/* Order Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Package Details
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="font-medium text-gray-900 dark:text-white">
                    Multiple Packages
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {recipients.length} recipients with individual package selection
                  </p>
                  <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {(() => {
                      const packageCounts = recipients.reduce((acc, r) => {
                        const pkgName = r.dataPackage?.name || 'Unknown';
                        acc[pkgName] = (acc[pkgName] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);
                      
                      return Object.entries(packageCounts).map(([name, count]) => (
                        <div key={name} className="flex items-start justify-between gap-3">
                          <span className="min-w-0 break-words">{name}:</span>
                          <span className="shrink-0">{count} ×</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Order Summary
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Total Recipients
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {recipients.length}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Total Amount
                  </p>
                  <p className="text-2xl font-bold text-primary-600">
                    GHS {calculateTotal().toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Select Payment Method
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button
                  onClick={() => setFormData({...formData, paymentMethod: 'wallet'})}
                  aria-pressed={formData.paymentMethod === 'wallet'}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    formData.paymentMethod === 'wallet'
                      ? 'border-primary-700 bg-primary-50/80 dark:bg-primary-900/25 ring-4 ring-primary-500/40 shadow-lg'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div
                    aria-hidden="true"
                    className={`absolute top-2 left-2 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      formData.paymentMethod === 'wallet'
                        ? 'border-primary-700 dark:border-primary-400 bg-primary-700 dark:bg-primary-400'
                        : 'border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/30'
                    }`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full bg-white transition-opacity ${
                        formData.paymentMethod === 'wallet' ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </div>
                  {formData.paymentMethod === 'wallet' && (
                    <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5 text-primary-600 dark:text-primary-400" />
                  )}
                  <CurrencyDollarIcon className="h-6 w-6 mx-auto mb-2 text-primary-600" />
                  <p className="font-medium text-gray-900 dark:text-white">Wallet</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Pay from wallet balance</p>
                </button>

                {paymentAvailability.paystackEnabled && (
                <button
                  onClick={() => {
                    setFormData({ ...formData, paymentMethod: 'paystack' });
                  }}
                  aria-pressed={formData.paymentMethod === 'paystack'}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    formData.paymentMethod === 'paystack'
                      ? 'border-primary-700 bg-primary-50/80 dark:bg-primary-900/25 ring-4 ring-primary-500/40 shadow-lg'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div
                    aria-hidden="true"
                    className={`absolute top-2 left-2 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      formData.paymentMethod === 'paystack'
                        ? 'border-primary-700 dark:border-primary-400 bg-primary-700 dark:bg-primary-400'
                        : 'border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/30'
                    }`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full bg-white transition-opacity ${
                        formData.paymentMethod === 'paystack' ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </div>
                  {formData.paymentMethod === 'paystack' && (
                    <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5 text-primary-600 dark:text-primary-400" />
                  )}
                  <CreditCardIcon className="h-6 w-6 mx-auto mb-2 text-primary-600" />
                  <p className="font-medium text-gray-900 dark:text-white">Paystack</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Pay with card/bank</p>
                </button>
                )}

                {paymentAvailability.mobileMoneyEnabled && (
                <button
                  onClick={() => {
                    setFormData({ ...formData, paymentMethod: 'mobile_money' });
                  }}
                  aria-pressed={formData.paymentMethod === 'mobile_money'}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    formData.paymentMethod === 'mobile_money'
                      ? 'border-primary-700 bg-primary-50/80 dark:bg-primary-900/25 ring-4 ring-primary-500/40 shadow-lg'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div
                    aria-hidden="true"
                    className={`absolute top-2 left-2 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      formData.paymentMethod === 'mobile_money'
                        ? 'border-primary-700 dark:border-primary-400 bg-primary-700 dark:bg-primary-400'
                        : 'border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/30'
                    }`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full bg-white transition-opacity ${
                        formData.paymentMethod === 'mobile_money' ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </div>
                  {formData.paymentMethod === 'mobile_money' && (
                    <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5 text-primary-600 dark:text-primary-400" />
                  )}
                  <PhoneIcon className="h-6 w-6 mx-auto mb-2 text-primary-600" />
                  <p className="font-medium text-gray-900 dark:text-white">Mobile Money</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">MTN/AirtelTigo/Telecel</p>
                </button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => setShowPreview(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={createBulkOrder}
                disabled={processingOrder}
                className="w-full sm:w-auto"
              >
                {processingOrder ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                )}
                Proceed to Payment
              </Button>
            </div>
          </div>
        </Modal>

        {/* Payment Modal */}
        <Modal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          title="Complete Payment"
          size="large"
        >
          {currentBulkOrder && (
            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  Order Details
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                  Order #{currentBulkOrder.orderNumber}
                </p>
                {createRequestId ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                    Request ID: {createRequestId}
                  </p>
                ) : null}
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentBulkOrder.totalRecipients} recipients
                </p>
                <p className="text-lg font-bold text-primary-600 mt-2">
                  GHS {currentBulkOrder.pricing.totalPrice.toFixed(2)}
                </p>
              </div>

              {createValidation?.rejected ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-2" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                        Some recipients were rejected
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        Accepted: {createValidation.accepted}. Rejected: {createValidation.rejected}.
                      </p>

                      <div className="mt-3 flex items-center justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            try {
                              const csv = bulkOrderService.exportRejectedRowsToCSV(createValidation);
                              const blob = new Blob([csv], { type: 'text/csv' });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `bulk-order-rejected-${currentBulkOrder.orderNumber}.csv`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              window.URL.revokeObjectURL(url);
                              toast.success('Rejected rows exported');
                            } catch (e) {
                              toast.error('Failed to export rejected rows');
                            }
                          }}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                          Export Rejected Rows
                        </Button>
                      </div>

                      <div className="mt-3 max-h-40 overflow-y-auto rounded-md bg-white/60 dark:bg-gray-900/30 border border-amber-200 dark:border-amber-800">
                        <div className="divide-y divide-amber-100 dark:divide-amber-900/40">
                          {(Array.isArray(createValidation.results) ? createValidation.results : [])
                            .filter((r) => r?.status === 'rejected')
                            .slice(0, 25)
                            .map((r) => (
                              <div key={`row-${r.index}`} className="px-3 py-2">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-medium text-gray-900 dark:text-white min-w-0 break-all">
                                    {r.input?.phone || `Row ${r.index + 1}`}
                                  </span>
                                  <span className="text-[11px] text-gray-600 dark:text-gray-300 break-words text-right flex-1">
                                    {r.reason}
                                  </span>
                                </div>
                              </div>
                            ))}

                          {(!createValidation.results || createValidation.results.length === 0) &&
                            (createValidation.invalidRecipients || []).slice(0, 25).map((r, idx) => (
                              <div key={`${r.phone || 'unknown'}-${idx}`} className="px-3 py-2">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-medium text-gray-900 dark:text-white min-w-0 break-all">
                                    {r.phone || `Row ${typeof r.index === 'number' ? r.index + 1 : idx + 1}`}
                                  </span>
                                  <span className="text-[11px] text-gray-600 dark:text-gray-300 break-words text-right flex-1">
                                    {r.reason}
                                  </span>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>

                      {((createValidation.results && createValidation.results.filter((r) => r.status === 'rejected').length) || (createValidation.invalidRecipients?.length || 0)) > 25 ? (
                        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-2">
                          Showing first 25 rejected recipients.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Click below to complete your payment
                </p>

                {formData.paymentMethod === PAYMENT_METHODS.MOBILE_MONEY && (
                  <div className="mb-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-900/20 p-4 text-left">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        label="Mobile Money Number"
                        value={mobileMoneyNumber}
                        onChange={(e) => setMobileMoneyNumber(sanitizePhoneInput(e.target.value))}
                        placeholder="0534788219"
                      />
                      <div className="form-group">
                        <label className="form-label">Network</label>
                        <select
                          value={mobileMoneyNetwork}
                          onChange={(e) => setMobileMoneyNetwork(e.target.value as any)}
                          className="form-input"
                        >
                          <option value="mtn">MTN</option>
                          <option value="airteltigo">AirtelTigo</option>
                          <option value="telecel">Telecel</option>
                        </select>
                        <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          Payment will be auto-detected within seconds.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  variant="primary"
                  size="lg"
                  onClick={processPayment}
                  disabled={processingOrder}
                  className="w-full sm:w-auto"
                >
                  {processingOrder ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : (
                    <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                  )}
                  Complete Payment
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>

      {/* Save as group modal */}
      <Modal
        isOpen={showSaveGroupModal}
        onClose={() => {
          setShowSaveGroupModal(false);
          if (pendingNavigateOrderId) navigate(`/bulk-orders/${pendingNavigateOrderId}`);
        }}
        title="Save Recipients as a Group?"
      >
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          Save these {recipients.length} recipients as a group for quick reuse in future bulk orders.
        </p>
        <Input
          placeholder="Group name (e.g. Work Team, Family)"
          value={saveGroupName}
          maxLength={50}
          onChange={e => setSaveGroupName(e.target.value)}
        />
        <div className="flex justify-end gap-3 mt-4">
          <Button
            variant="outline"
            onClick={() => {
              setShowSaveGroupModal(false);
              if (pendingNavigateOrderId) navigate(`/bulk-orders/${pendingNavigateOrderId}`);
            }}
          >
            Skip
          </Button>
          <Button
            variant="primary"
            disabled={savingGroup || !saveGroupName.trim()}
            onClick={async () => {
              if (!saveGroupName.trim()) return;
              setSavingGroup(true);
              try {
                await savedGroupsAPI.create({
                  name: saveGroupName.trim(),
                  members: recipients.map(r => ({
                    phone: r.phone,
                    name: '',
                    network: (r.network || 'mtn') as 'mtn' | 'airteltigo' | 'telecel'
                  }))
                });
                toast.success(`Group "${saveGroupName.trim()}" saved`);
              } catch {
                toast.error('Could not save group, but your order was placed');
              } finally {
                setSavingGroup(false);
                setShowSaveGroupModal(false);
                if (pendingNavigateOrderId) navigate(`/bulk-orders/${pendingNavigateOrderId}`);
              }
            }}
          >
            {savingGroup ? <LoadingSpinner size="sm" className="mr-2" /> : null}
            Save Group
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default BulkOrderPage;
